/**
 * Adyen Client
 *
 * Functions for creating and managing Adyen checkout instances.
 * Separated per architecture spec: lib/checkout/payments/adyen/client.ts
 */

import type { UIElement } from '@adyen/adyen-web';

import { getLanguageFromLocale } from '@/lib/i18n/config';

import { PAYMENT_TYPES, type PaymentType } from '../types';
import type { AdyenCheckoutOpts, AdyenEnv, INonAdyenGatewayCheckout } from './types';

// Environment configuration
const ADYEN_ENV = process.env.NEXT_PUBLIC_ADYEN_ENV || 'test';
const ADYEN_CLIENT_KEY = process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY || '';
const GOOGLE_PAY_ENV = process.env.NEXT_PUBLIC_GOOGLE_PAY_ENV || 'TEST';
const GOOGLE_PAY_MERCHANT_ID = process.env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_ID || '';
const GOOGLE_PAY_MERCHANT_NAME = process.env.NEXT_PUBLIC_GOOGLE_PAY_MERCHANT_NAME || 'Gallet';
const IS_BROWSER = typeof window !== 'undefined';

/** Adyen SDK telemetry — enabled in production only; off in local dev */
export const ADYEN_ANALYTICS_CONFIG = {
  enabled: process.env.NODE_ENV === 'production',
};

// Merchant account mappings
const ADYEN_EU_ACCOUNTS = (
  process.env.NEXT_PUBLIC_ADYEN_EU_ACCOUNTS || 'AT,BE,DE,DK,ES,FR,IT,NL,PT'
).split(',');
const ADYEN_MERCHANT_ACCOUNT_EU = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_EU || '';
const ADYEN_MERCHANT_ACCOUNT_US = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_US || '';
const ADYEN_MERCHANT_ACCOUNT_CA = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_CA || '';
const ADYEN_MERCHANT_ACCOUNT_CH = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_CH || '';
const ADYEN_MERCHANT_ACCOUNT_UK = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_UK || '';
const ADYEN_MERCHANT_ACCOUNT_JP = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_JP || '';
const ADYEN_MERCHANT_ACCOUNT_AU = process.env.NEXT_PUBLIC_ADYEN_MERCHANT_ACCOUNT_AU || '';

// Language slug → Adyen locale
const langToAdyenLocale: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  pt: 'pt-PT',
  ru: 'ru-RU',
  zh: 'zh-CN',
  zht: 'zh-TW',
};

/**
 * Resolve Adyen locale from the app URL locale (e.g. `be-fr`, `us-en`).
 *
 * Uses `getLanguageFromLocale` for language-slug normalization (e.g. `jp` → `ja`),
 * then maps to an Adyen-compatible BCP 47 locale string.
 */
export const resolveAdyenLocale = (urlLocale: string): string => {
  const lang = getLanguageFromLocale(urlLocale);
  return langToAdyenLocale[lang] || 'en-US';
};

/**
 * Get merchant account based on country code
 */
export const getMerchantAccount = (countryCode: string): string => {
  if (ADYEN_EU_ACCOUNTS.includes(countryCode)) {
    return ADYEN_MERCHANT_ACCOUNT_EU;
  }

  switch (countryCode) {
    case 'US':
      return ADYEN_MERCHANT_ACCOUNT_US;
    case 'CA':
      return ADYEN_MERCHANT_ACCOUNT_CA;
    case 'CH':
      return ADYEN_MERCHANT_ACCOUNT_CH;
    case 'UK':
    case 'GB':
      return ADYEN_MERCHANT_ACCOUNT_UK;
    case 'JP':
      return ADYEN_MERCHANT_ACCOUNT_JP;
    case 'AU':
      return ADYEN_MERCHANT_ACCOUNT_AU;
    default:
      return ADYEN_MERCHANT_ACCOUNT_US;
  }
};

type FormatMessage = (msg: { id?: string; defaultMessage?: string }) => string;

const defaultFormatMessage: FormatMessage = (msg) => msg.defaultMessage || '';

/**
 * Get Adyen component-specific configuration
 */
export const getAdyenComponentConfig = (
  type: PaymentType,
  price: { amount: number; currency: string },
  countryCode: string,
  formatMessage: FormatMessage = defaultFormatMessage
): Record<string, unknown> => {
  const cardStyles = {
    base: {
      fontSize: '16px',
      fontFamily: '"Moderat", sans-serif',
      fontWeight: '300',
      color: '#3e4827',
      lineHeight: '1',
      caretColor: '#3e4827',
    },
    placeholder: {
      fontSize: '16px',
      fontWeight: '300',
      color: '#3e4827',
    },
    error: {
      color: '#c00',
    },
    validated: {
      color: '#3e4827',
    },
  };

  const baseStyles = {
    base: {
      fontSize: '16px',
      fontFamily: '"Moderat", sans-serif',
      fontWeight: '400',
      color: '#3e4827',
      padding: '0',
    },
    placeholder: {
      fontSize: '16px',
      fontWeight: '400',
      color: '#3e4827',
    },
    error: {
      color: '#c00',
    },
    validated: {
      color: '#3e4827',
    },
  };

  switch (type) {
    case 'card':
    case 'scheme':
      return {
        hasHolderName: true,
        holderNameRequired: true,
        showStoredPaymentMethods: false,
        autoFocus: false,
        showBrandIcon: true,
        enableStoreDetails: false,
        amount: {
          value: 0,
          currency: price.currency,
        },
        styles: cardStyles,
        placeholders: {
          cardNumber: formatMessage({
            id: 'payment.creditCardNumberPlaceholder',
            defaultMessage: 'Credit Card number*',
          }),
          expiryDate: formatMessage({
            id: 'payment.creditCardExpiryDatePlaceholder',
            defaultMessage: 'Expiry date*',
          }),
          securityCode: formatMessage({
            id: 'payment.creditCardSecurityCodePlaceholder',
            defaultMessage: 'Security code*',
          }),
          securityCodeThreeDigits: formatMessage({
            id: 'payment.creditCardSecurityCodePlaceholder',
            defaultMessage: 'Security code*',
          }),
          securityCodeFourDigits: formatMessage({
            id: 'payment.creditCardSecurityCodePlaceholder',
            defaultMessage: 'Security code*',
          }),
          holderName: formatMessage({
            id: 'payment.creditCardHolderNamePlaceholder',
            defaultMessage: 'Full name*',
          }),
        },
      };
    case 'paywithgoogle':
    case 'googlepay':
      return {
        environment: GOOGLE_PAY_ENV,
        configuration: {
          gatewayMerchantId: getMerchantAccount(countryCode),
          merchantId: GOOGLE_PAY_MERCHANT_ID,
          merchantName: GOOGLE_PAY_MERCHANT_NAME,
        },
        countryCode,
        amount: {
          value: price.amount,
          currency: price.currency,
        },
        showPayButton: false,
      };
    case 'applepay_express':
      return {
        showPayButton: true,
        isExpress: true,
        userAction: 'continue',
        requiredBillingContactFields: ['postalAddress', 'name', 'phoneticName', 'phone', 'email'],
        requiredShippingContactFields: ['postalAddress', 'name', 'phoneticName', 'phone', 'email'],
        intent: 'authorize',
        buttonColor: 'white',
      };
    case 'applepay':
      return {
        showPayButton: false,
      };
    case 'bcmc':
      return {
        showPayButton: false,
        styles: cardStyles,
        placeholders: {
          cardNumber: formatMessage({
            id: 'payment.creditCardNumberPlaceholder',
            defaultMessage: 'Credit Card number*',
          }),
          expiryDate: formatMessage({
            id: 'payment.creditCardExpiryDatePlaceholder',
            defaultMessage: 'Expiry date*',
          }),
        },
      };
    case 'paypal_express':
      return {
        isExpress: true,
        showPayButton: true,
        intent: 'authorize',
        userAction: 'continue',
        blockPayPalVenmoButton: true,
        blockPayPalCreditButton: true,
        blockPayPalPayLaterButton: true,
        style: {
          layout: 'horizontal',
          color: 'white',
          shape: 'sharp',
          height: 44,
          tagline: false,
          disableMaxWidth: true,
        },
      };
    default:
      return {
        styles: baseStyles,
      };
  }
};

/**
 * Create Adyen checkout instance
 */
export const createAdyenCheckout = async ({
  clientKey,
  countryCode,
  formatMessage,
  locale,
  paymentMethodType,
  price,
  session,
  showPayButton = true,
  ...handlers
}: AdyenCheckoutOpts): Promise<unknown> => {
  // Dynamically import Adyen to avoid SSR issues
  // oxlint-disable-next-line typescript/no-explicit-any
  const AdyenCheckoutModule = (await import('@adyen/adyen-web')) as any;
  const AdyenCheckout = AdyenCheckoutModule.default ?? AdyenCheckoutModule.AdyenCheckout;

  const cardNumberPlaceholder = formatMessage({
    id: 'payment.creditCardNumberPlaceholder',
    defaultMessage: 'Credit Card number*',
  });
  const expiryDatePlaceholder = formatMessage({
    id: 'payment.creditCardExpiryDatePlaceholder',
    defaultMessage: 'Expiry date*',
  });
  const securityCodePlaceholder = formatMessage({
    id: 'payment.creditCardSecurityCodePlaceholder',
    defaultMessage: 'Security code*',
  });
  const holderNamePlaceholder = formatMessage({
    id: 'payment.creditCardHolderNamePlaceholder',
    defaultMessage: 'Full name*',
  });

  const translations = {
    [locale]: {
      'creditCard.cvcField.title': formatMessage({
        id: 'payment.creditCardSecurityCode',
        defaultMessage: 'Security code*',
      }),
      'creditCard.cvcField.placeholder.3digits': securityCodePlaceholder,
      'creditCard.cvcField.placeholder.4digits': securityCodePlaceholder,
      'creditCard.expiryDateField.title': formatMessage({
        id: 'payment.creditCardExpiryDate',
        defaultMessage: 'Expiry date*',
      }),
      'creditCard.expiryDateField.placeholder': expiryDatePlaceholder,
      'creditCard.numberField.title': formatMessage({
        id: 'payment.creditCardNumber',
        defaultMessage: 'Card number*',
      }),
      'creditCard.numberField.placeholder': cardNumberPlaceholder,
      'creditCard.holderName': formatMessage({
        id: 'payment.creditCardHolderName',
        defaultMessage: 'Full name*',
      }),
      'creditCard.holderName.placeholder': holderNamePlaceholder,
      ...(paymentMethodType === PAYMENT_TYPES.BANCONTACT
        ? {
            'creditCard.numberField.title': formatMessage({
              id: 'payment.cardNumber',
              defaultMessage: 'Card number',
            }),
          }
        : {}),
    },
  };

  // Build configuration - not using CoreConfiguration type due to handler type mismatch
  // oxlint-disable-next-line typescript/no-explicit-any
  const configuration: any = {
    amount: {
      value: price.amount,
      currency: price.currency,
    },
    environment: ADYEN_ENV as AdyenEnv,
    clientKey: clientKey || ADYEN_CLIENT_KEY,
    analytics: ADYEN_ANALYTICS_CONFIG,
    locale,
    translations,
    showPayButton,
    countryCode,
    ...(session ? { session } : {}),
    ...handlers,
  };

  return await AdyenCheckout(configuration);
};

/**
 * Create Adyen payment component
 */
export const createAdyenComponent = async (
  paymentType: PaymentType,
  checkoutInstance: unknown,
  config: Record<string, unknown>
): Promise<UIElement> => {
  const AdyenModule = await import('@adyen/adyen-web');

  const adyenComponentMap: Partial<Record<PaymentType, unknown>> = {
    // Affirm uses redirect flow (like Breitling website) - user goes to affirm.com
    affirm: AdyenModule.Redirect,
    applepay: AdyenModule.ApplePay,
    applepay_express: AdyenModule.ApplePay,
    bcmc: AdyenModule.Bancontact,
    card: AdyenModule.Card,
    facilypay_3x: AdyenModule.Redirect,
    facilypay_4x: AdyenModule.Redirect,
    googlepay: AdyenModule.GooglePay,
    ideal: AdyenModule.Redirect,
    klarna_paynow: AdyenModule.Klarna,
    paypal: AdyenModule.PayPal,
    paypal_express: AdyenModule.PayPal,
    paybybank: AdyenModule.PayByBank,
    scheme: AdyenModule.Card,
    twint: AdyenModule.Twint,
    visa: AdyenModule.Card,
    zip: AdyenModule.Redirect,
    alipay: AdyenModule.Redirect,
    paywithgoogle: AdyenModule.GooglePay,
    unionpay: AdyenModule.Card,
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const ComponentClass = adyenComponentMap[paymentType] as any;

  if (!ComponentClass) {
    throw new Error(`Unsupported Adyen payment type: ${paymentType}`);
  }
  return new ComponentClass(checkoutInstance, config);
};

// Apple Pay availability cache
let cachedApplePayAvailability: boolean | null = null;

/**
 * Check if Apple Pay is available
 */
export const checkApplePayAvailability = async (): Promise<boolean> => {
  if (!IS_BROWSER) {
    return false;
  }

  if (cachedApplePayAvailability !== null) {
    return cachedApplePayAvailability;
  }

  // Quick Safari check (native Apple Pay)
  if (
    typeof window !== 'undefined' &&
    'ApplePaySession' in window &&
    // oxlint-disable-next-line typescript/no-explicit-any
    (window as any).ApplePaySession?.canMakePayments?.()
  ) {
    cachedApplePayAvailability = true;
    return true;
  }

  try {
    // oxlint-disable-next-line typescript/no-explicit-any
    const AdyenCheckoutModule = (await import('@adyen/adyen-web')) as any;
    const AdyenCheckout = AdyenCheckoutModule.default ?? AdyenCheckoutModule.AdyenCheckout;
    const ApplePay = AdyenCheckoutModule.ApplePay;

    const adyenCheckout = await AdyenCheckout({
      environment: ADYEN_ENV as AdyenEnv,
      clientKey: ADYEN_CLIENT_KEY,
      analytics: ADYEN_ANALYTICS_CONFIG,
      countryCode: 'US',
      amount: {
        value: 0,
        currency: 'USD',
      },
    });

    const adyenApplePayComponent = new ApplePay(adyenCheckout, {
      showPayButton: false,
    });

    await adyenApplePayComponent.isAvailable();
    cachedApplePayAvailability = true;
  } catch {
    cachedApplePayAvailability = false;
  }

  return cachedApplePayAvailability;
};

/**
 * In-memory adapter mimicking Adyen Drop-in interface for form-backed financing methods.
 *
 * Bridges custom React forms to {@link usePaymentProcessor.processNonAdyenPayment}:
 * - {@link INonAdyenGatewayCheckout.setValidateFunction} — form validation on Pay click
 * - {@link INonAdyenGatewayCheckout.setState} / `getState` — payment payload (e.g. `cembraData`)
 * - `paymentType` — fixed identity for routing and form branching
 *
 * Lifecycle is owned by {@link useNonAdyenGateway} in the page shell.
 *
 * @param paymentType - Payment method type baked into the instance
 * @returns Gateway adapter implementing {@link INonAdyenGatewayCheckout}
 */
export const NonAdyenGatewayCheckout = ({
  paymentType,
}: {
  paymentType: PaymentType;
}): INonAdyenGatewayCheckout => {
  let state: Record<string, unknown> = {};
  let validated = false;
  let validateFn: (() => boolean) | null = null;
  let inlineValidationError = false;

  return {
    paymentType,
    getState: () => state,
    setState: (newState: Record<string, unknown>) => {
      state = { ...state, ...newState };
    },
    setIsValid: (valid: boolean) => {
      validated = valid;
    },
    isValid: () => {
      inlineValidationError = false;
      if (validateFn) {
        return validateFn();
      }
      return validated;
    },
    setValidateFunction: (fn: () => boolean) => {
      validateFn = fn;
    },
    failWithInlineError: () => {
      inlineValidationError = true;
      return false;
    },
    hasInlineValidationError: () => inlineValidationError,
    unmount: () => {
      // No-op for compatibility
    },
  };
};
